<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { differenceInSeconds } from 'date-fns';
import { useApi } from '@/api';
import UploadFileComponent from '@/components/UploadFile.vue';
import type { PutCommentsByIdData } from '@/types/openapi-ts';
import type UiScrollbar from '@/components/UIElements/UiScrollbar.vue';
import type { UploadedFile } from '@/types/core';
//#endregion

//#region types
export type CommentUpdateBody = PutCommentsByIdData['body'];
type CommentFile = {
  id: string;
  filename: string;
  bytes: number;
  mime_type: string | null;
  download_url: string;
  preview_url: string | null;
};
type CommentType = 'user' | 'system' | 'all';
interface CommentTypeOption {
  id: CommentType;
  name: string;
}

interface CommentItem {
  created_at: string | null;
  id: string;
  type: 'user' | 'system';
  content: string;
  author_type: 'admin' | 'affiliate';
  is_owner: boolean;
  admin_name: string | null;
  files: CommentFile[];
}

export type GetMethodType = () => Promise<{ data: { payload: CommentItem[] } }>;

interface Props {
  getMethod: GetMethodType;
  postMethod: (comment: CommentUpdateBody) => void;
  isEditable?: boolean;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  isEditable: false,
});
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
//#endregion

//#region state
const postsRef = ref<InstanceType<typeof UiScrollbar> | null>(null);
const isLoading = ref(true);
const currentCommentsType = ref<CommentType>('user');
const commentsList = ref<CommentItem[]>([]);
const commentId = ref('');
const error = ref<string[]>([]);
const comment = ref<CommentUpdateBody>({
  content: '',
  file_ids: [],
});
const fileList = ref<UploadedFile[]>([]);
//#endregion

//#region computed
const isSystem = computed(() => currentCommentsType.value === 'system');

const commentsTypes = computed<CommentTypeOption[]>(() => ([
  {
    id: 'user',
    name: t('mediaBuying.comments.types.user'),
  },
  {
    id: 'system',
    name: t('mediaBuying.comments.types.system'),
  },
  {
    id: 'all',
    name: t('mediaBuying.comments.types.all'),
  },
]));

const filteredComments = computed(() => {
  let result = commentsList.value;

  if (currentCommentsType.value === 'all') {
    return result;
  }

  return commentsList.value.filter((item) => item.type === currentCommentsType.value);
});
//#endregion

//#region methods
async function getComments() {
  try {
    const { data: { payload } } = await props.getMethod();
    commentsList.value = payload;

    await nextTick();
    scrollToEnd();
  } finally {
    isLoading.value = false;
  }
}

function scrollToEnd() {
  const postsBlock = postsRef.value?.scrollBarRef?.wrapRef;

  if (!postsBlock) {
    return;
  }

  postsBlock.scrollTop = postsBlock.scrollHeight;
}

async function deleteComment(id: string) {
  isLoading.value = true;
  await api.deleteComment(id);
  await getComments();
}

function editComment(id: string, content: string, files: CommentFile[]) {
  commentId.value = id;
  comment.value.content = content;
  if (files.length > 0) {
    files.forEach((file) => {
      fileList.value.push({ name: file.filename, id: file.id });
    });
  }
}

function isCommentOwner(commentItem: CommentItem) {
  return commentItem.is_owner && commentItem.type !== 'system';
}

function checkDate(date: string | null) {
  return differenceInSeconds(new Date(), new Date(date || '')) < 86400;
}

async function uploadFilesAction(asset: UploadedFile) {
  fileList.value.push(asset);
}

function removeFile(file: UploadedFile) {
  const fileListIndex = fileList.value.findIndex((item) => item.uid === file.uid);
  if (fileListIndex !== -1) {
    fileList.value.splice(fileListIndex, 1);
  }
}

function toDefault() {
  comment.value.content = '';
  comment.value.file_ids = [];
  fileList.value = [];
  commentId.value = '';

  nextTick(() => {
    scrollToEnd();
  });
}

async function submitComment() {
  isLoading.value = true;
  error.value = [];

  try {
    if (fileList.value.length > 0) {
      for (const item of fileList.value) {
        let currentId = item.id || '';
        if(!item.id) {
          const formData = new FormData();
          formData.append('files', item as Blob);
          const response = await api.postFile(formData as unknown as Record<string, File | Blob>, () => {});
          currentId = response.data.payload.files?.id!;
        }
        comment.value.file_ids.push(currentId);
      }
    }
    if (commentId.value) {
      await api.updateComment(commentId.value, comment.value);
    } else {
      await props.postMethod(comment.value);
    }
  } catch (e) {
    // console.log(e);
    // error.value.push(`File "${options.file.name}" is not valid`);
  } finally {
    toDefault();
    await getComments();
    isLoading.value = false;
  }
}
//#endregion

//#region hooks
getComments();
//#endregion
</script>

<template>
  <div
    v-loading="isLoading"
    class="comments"
  >
    <div class="t:flex t:gap-8 t:mb-12">
      <CardTitle
        class="t:flex-1"
        :title="$t('mediaBuying.comments.title')"
      />
      <UiSelectElement
        v-model="currentCommentsType"
        :list="commentsTypes"
        size="big"
        :width="170"
      />
    </div>
    <UiScrollbar
      v-if="filteredComments.length > 0"
      ref="postsRef"
      wrap-style="max-height: 320px;"
      class="comments__scroll t:mb-12"
    >
      <div class="comments__post-list t:space-y-12">
        <div
          v-for="commentItem in filteredComments"
          :key="commentItem.id"
          :class="[
            't:flex',
            {'t:justify-end': isCommentOwner(commentItem)}
          ]"
        >
          <div
            :class="[
              'comments__post-item t:space-y-8',
              {'comments__post-item_edit': commentItem.id === commentId},
              {'comments__post-item_system': commentItem.type === 'system'},
            ]"
          >
            <div class="t:space-y-4">
              <div class="t:flex t:items-center t:justify-between">
                <div class="comments__post-item-info t:space-x-8">
                  <div class="t:flex">
                    <UiIcon
                      name="user"
                      color="inherit"
                      substyle="far"
                      class="t:mr-4"
                    />
                    {{ commentItem.admin_name }}
                  </div>
                  <UiSeparator
                    type="dot"
                    color="--background-primary"
                  />
                  <div class="t:space-x-4 t:flex">
                    <UiIcon
                      :name="commentItem.type === 'system' ? 'history' : 'comment'"
                      color="inherit"
                      substyle="far"
                      class="t:mr-4"
                    />
                    {{ commentItem.type === 'system' ? $t('mediaBuying.comments.types.system') : $t('mediaBuying.comments.types.comment') }}
                  </div>
                </div>
                <div
                  v-if="checkDate(commentItem.created_at)
                    && isCommentOwner(commentItem)
                    && commentItem.id !== commentId
                    && isEditable"
                  class="t:flex t:items-center t:gap-8 t:ml-8"
                >
                  <UiAction
                    icon="pen"
                    @click="editComment(commentItem.id, commentItem.content, commentItem.files)"
                  />
                  <UiAction
                    icon="trash-alt"
                    color="var(--text-negative)"
                    @click="deleteComment(commentItem.id)"
                  />
                </div>
              </div>
              <div class="comments__post-item-date">
                <UiIcon
                  name="calendar-alt"
                  color="inherit"
                  class="t:mr-4"
                />
                {{ $filters.formatDate3(commentItem.created_at || '') }}
              </div>
              <div class="comments__post-item-content">
                {{ commentItem.content }}
              </div>
            </div>
            <div class="comments__post-item-files">
              <a
                v-for="file in commentItem.files"
                :key="file.id"
                :href="file.download_url"
                :download="file.filename"
                target="_blank"
                class="comments__post-item-link"
              >
                {{ file.filename }}
                <UiIcon
                  name="download"
                  color="inherit"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </UiScrollbar>
    <div class="comments__upload">
      <UploadFileComponent
        v-model="fileList"
        title=""
        :accept="undefined"
        :disabled="!isEditable || isSystem"
        :upload-files-action="uploadFilesAction"
        :remove-file="removeFile"
      >
        <template v-slot>
          <UiButton
            icon="paperclip"
            is-icon
          />
        </template>
        <template v-slot:content>
          <UiTextarea
            v-model="comment.content"
            :placeholder="$t(`mediaBuying.comments.placeholder`)"
            :rows="3"
            maxlength="1023"
            class="t:flex-1"
            :disabled="!isEditable || isSystem"
          />
          <UiButton
            v-if="commentId"
            type="negative"
            icon="times"
            is-icon
            :disabled="!isEditable"
            @click="toDefault()"
          />
          <UiButton
            :disabled="comment.content === '' || isSystem"
            filled
            icon="check"
            type="positive"
            @click="submitComment()"
          >
            {{ commentId ? $t(`mediaBuying.comments.button_update`) : $t(`mediaBuying.comments.button_create`) }}
          </UiButton>
        </template>
      </UploadFileComponent>
    </div>
  </div>
</template>

<style lang="scss">
  .comments {
    &__scroll{
      height: initial !important;
    }
    &__post-list {
      flex-direction: column;
      display: flex;
    }
    &__post-item {
      max-width: 90%;
      padding: 12px;
      border-radius: 8px;
      background: var(--background-positive-subtle);
      position: relative;
      white-space: pre-line;
      min-width: 150px;
      border: 1px solid transparent;

      &_edit{
        border-color: var(--border-warning);
        background: var(--background-warning-subtle);
      }
      &_system{
        border-color: var(--border-default);
        background: var(--background-body);
      }
    }
    &__post-item-info {
      color: var(--text-body-secondary);
      font-size: 12px;
      display: flex;
      align-items: center;
    }
    &__post-item-content {
      color: var(--text-body);
      font-size: 14px;
      line-height: 18px; /* 128.571% */
    }
    &__post-item-date {
      display: flex;
      align-items: center;
      color: var(--text-primary);
      font-size: 12px;
    }
    &__post-item-files {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }
    &__post-item-link {
      display: flex;
      padding: 6px 16px;
      align-items: center;
      gap: 8px;
      border-radius: 5px;
      border: 1px solid var(--border-divider);
      background: var(--background-body);
      color: var(--text-link);
      font-size: 12px;
      font-weight: 500;
    }
  }
</style>
